// CSS composition helper for hybrid-preloaded runtime sources.
// It keeps only active initial-observed rules in the host style tag.
import type { LuisCompileDebugRule, LuisCompileResult } from '@lentystyle/compiler'
import { renderCssFromLeaves, type LuisRenderLeaf } from '@lentystyle/compiler/renderer'
import type { LuisDebugFeatureApi } from '../../features'
import { buildDebugRulesByOrderedKeys } from './css-compose'

// Compose only active initial-observed CSS for sources already preloaded globally.
export function composeInitialObservedCssInSourceOrder(
  result: LuisCompileResult,
  activeInitialObservedRules: Array<{ key: string }>,
  remappedDebugRules: LuisCompileDebugRule[] | undefined,
  compileDebugEnabled: boolean,
  debugFeature: LuisDebugFeatureApi | undefined,
): string {
  const activeInitialKeys = new Set(activeInitialObservedRules.map((rule) => rule.key))
  // Fall back to initial observed rule text when ordered metadata is unavailable.
  if (!result.mainRules) {
    return activeInitialObservedRules
      .map((rule) => result.initialObservedRules?.find((entry) => entry.key === rule.key)?.rule ?? '')
      .filter(Boolean)
      .join('\n\n')
  }

  const orderedRules = result.mainRules.filter((rule) => rule.observedMode === 'initial' && activeInitialKeys.has(rule.key))
  const pseudoLeaves: LuisRenderLeaf[] = orderedRules.map((rule) => ({
    key: rule.key,
    atRules: [...rule.atRules],
    selector: rule.selector,
    observedMode: 'none',
    sourceLine: rule.line,
    declarations: [],
  }))
  const overrides = new Map<string, string>(orderedRules.map((rule) => [rule.key, rule.rule]))
  const baseCss = renderCssFromLeaves([], pseudoLeaves, overrides).filter(Boolean).join('\n\n')

  // Return plain css when there is nothing to mark or debug is disabled.
  if (!baseCss || !compileDebugEnabled || !debugFeature) {
    return baseCss
  }

  const orderedDebugRules = buildDebugRulesByOrderedKeys(remappedDebugRules, orderedRules.map((rule) => rule.key))
  // Return initial-observed css with ordered debug markers applied.
  return debugFeature.applyDebugRulesToCssMarkers(baseCss, orderedDebugRules) ?? baseCss
}
